import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Definitions } from '../../core/definitions';
import { IAgentInfo } from '../../core/interfaces/agent-info.interface';
import { LendingOptions } from '../../core/interfaces/lending-options.interface';
import { LendingResponse } from '../../core/interfaces/lending-response.interface';
import { ApiService } from '../apiservice/apiservice.service';
import { ConfigService } from '../config/config.service';
import { StorageService } from '../storage/storage.service';
import { ApiResponse } from '../api-response.interface';
import { UserBorrowerPolicy } from '../../core/interfaces/borrow-policy.interface';
import { SharedService } from '../shared/shared.service';

@Injectable({
  providedIn: 'root',
})
export class IllSettingsService {
  constructor(
    private _apiService: ApiService,
    private _configService: ConfigService,
    private _http: HttpClient,
    private _storageService: StorageService,
    private _sharedService: SharedService,
  ) {}

  private _zeroResultsPostBodyKey = '_zres_pb';
  borrowerPolicy$ = new BehaviorSubject<any>(null);
  zeroResultsPB$ = new BehaviorSubject<any>(null);

  getBorrowPolicyBSValue() {
    return this.borrowerPolicy$.getValue();
  }

  setBorrowPolicyBS(value) {
    this.borrowerPolicy$.next(value);
  }

  setZeroResultsPostBodyCache(data) {
    this._storageService.setItem(this._zeroResultsPostBodyKey, data);
  }

  getZeroResultsPostBodyCache() {
    return this._storageService.getItem(this._zeroResultsPostBodyKey);
  }

  removeZeroResultsPostBodyCache(): void {
    this._storageService.remove(this._zeroResultsPostBodyKey);
  }

  XsetAgentInfoInCache(data) {
    this._storageService.setItem(Definitions.agentInfoSessionKey, data);
  }

  XgetAgentInfoInCache(): IAgentInfo {
    return this._storageService.getItem(Definitions.agentInfoSessionKey);
  }

  XgetAgentUrl(): string {
    return this._storageService.getItem(Definitions.agentInfoSessionKey).agentBaseUrl;
  }

  XgetAgentTokenCache(): string {
    return this._storageService.getItem(Definitions.agentInfoSessionKey).agentToken;
  }

  XupdateAgentTokenCache(token) {
    const info = this._storageService.getItem(Definitions.agentInfoSessionKey);
    if (info) {
      info.agentToken = token;
      this.XsetAgentInfoInCache(info);
    }
  }

  /**
   * Checks the borrower policy for the format and returns true if the format is allowed for the user type
   * @param format
   * @returns
   */
  showFormat(format) {
    const userData = this._storageService.getUserData();
    const borrowFormats = this.getBorrowPolicyBSValue();
    const matchedFormat = borrowFormats?.find(
      (x) => x.formatCode.toLowerCase() === format.toLowerCase(),
    );

    let show = false;

    if (matchedFormat) {
      if (userData?.isGuest) {
        show = matchedFormat?.forGuest;
      }

      if (userData?.isPatron) {
        show = matchedFormat?.forPatron;
      }

      if (userData?.isStaff) {
        show = matchedFormat?.forStaff;
      }
    }

    return show;
  }

  showFingerIcon(): boolean {
    let showFinger = true;
    const userData = this._storageService.getUserData();
    const isStaff = userData.isStaff;
    const illOptions = userData.illOptions;
    if ((isStaff || !illOptions.requestThisRequireLogin) && !illOptions.showRequestThis) {
      showFinger = false;
    }
    return showFinger;
  }

  //#region http calls

  getBorrowerPolicy(): Observable<UserBorrowerPolicy[]> {
    const url = `${this._configService.agApi}/illSettings/borrowerPolicyForAllUsers`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<UserBorrowerPolicy[]>) => {
        if (response.data) {
          const policies = this._sharedService.sortArray(response.data, 'format');
          this.setBorrowPolicyBS(policies);
          return policies;
        } else {
          throw new Error('Unable to fetch borrower policy.');
        }
      }),
    );
  }

  updateBorrowerPolicy(payload: any): Observable<UserBorrowerPolicy[]> {
    const url = `${this._configService.agApi}/illSettings/borrowerPolicyForAllUsers`;
    return this._apiService.post(url, payload).pipe(
      map((response: ApiResponse<UserBorrowerPolicy[]>) => {
        if (response.data) {
          const policies = this._sharedService.sortArray(response.data, 'format');
          this.setBorrowPolicyBS(policies);
          return policies;
        } else {
          throw new Error('Unable to update borrower policy.');
        }
      }),
    );
  }

  getLendingPolicy(): Observable<LendingResponse> {
    const url = `${this._configService.agApi}/illSettings/lendingpolicy`;
    return this._http.get<LendingResponse>(url);
  }

  updateLendingPolicy(body: LendingOptions): Observable<LendingResponse> {
    const url = `${this._configService.agApi}/illSettings/lendingpolicy`;
    return this._http.put<LendingResponse>(url, body);
  }

  getLendingPolicyShareIt(baseUrl: string): Observable<LendingResponse> {
    const url = `${baseUrl}/illSettings/lendingpolicy?agent=`; // passing date so this route can be identified in interceptor.
    return this._http.get<LendingResponse>(url);
  }

  updateLendingPolicyShareIt(baseUrl: string, body: LendingOptions): Observable<LendingResponse> {
    const url = `${baseUrl}/illSettings/lendingpolicy?agent=`; // passing date so this route can be identified in interceptor.
    return this._http.put<LendingResponse>(url, body);
  }

  getIllZeroSearchResults(data): Observable<any> {
    const url = `${this._configService.agApi}/log/zeroResults`;
    return this._apiService.post(url, data);
  }

  //#endregion
}
